import React, { useRef, useState } from 'react';
import { Formik } from 'formik';
import { Form, Input } from 'formik-antd';
import * as Yup from 'yup';
import { Button, Modal, Input as AntdInput } from 'antd';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import FormItem from 'components/formik/FormItem/FormItem';
import SubmitButton from 'components/formik/SubmitButton/SubmitButton';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import {
  checkInvitedUser,
  sendInviteUser,
} from 'redux/reducers/recruiterSlice';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BaseSectionMessage from 'components/wrappers/BaseSectionMessage';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from './IntiteUserModal.module.less';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
};

const validationSchema = Yup.object({
  email: Yup.string()
    .email('Email format is invalid')
    .required('Field is required')
    .max(120, 'Email should be 120 characters maximum'),
});

type SendInviteUser = {
  email: string;
};
const initialValues: SendInviteUser = {
  email: '',
};

export default function InviteUserModal(props: Props) {
  const { onClose, onSuccess } = props;
  const [linkToInvitation, setLinkToInvitation] = useState('');
  const [hasInvitationLinkError, setHasInvitationLinkError] = useState(false);
  const dispatch = useAppDispatch();
  const formRef: any = useRef();

  const getResetInvitationUserConfirmationModal = (email: string) => {
    return Modal.confirm({
      title: 'Do you really want to send the invitation once again?',
      icon: <span className="icon icon-attention-filled" />,
      okText: <span data-id="resetInvitationUserBtn">Send</span>,
      async onOk() {
        await handleSendInvite(email);
      },
      onCancel() {},
    });
  };

  const handleCopyLinkToClipboard = (text: string, result: boolean) => {
    if (result) {
      return BaseAlert.success(
        'Invitation link has been copied to the clipboard.',
      );
    } else {
      return BaseAlert.error('Something went wrong, please try again.');
    }
  };

  const handleSendInvite = async (email: string) => {
    const result = await dispatch(sendInviteUser(email));

    if (sendInviteUser.fulfilled.match(result)) {
      setHasInvitationLinkError(false);
      setLinkToInvitation(result.payload);
      onSuccess();
    } else {
      setHasInvitationLinkError(true);
    }
  };

  const handleSubmit = async (value: SendInviteUser) => {
    const result = await dispatch(checkInvitedUser(value.email));

    if (checkInvitedUser.fulfilled.match(result)) {
      if (result.payload?.isRegistred)
        return BaseAlert.error('User already registered.');
      if (result.payload?.isInvitedUser) {
        getResetInvitationUserConfirmationModal(value.email);
      } else {
        handleSendInvite(value.email);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleCloseModal = () => {
    if (linkToInvitation) return onClose();
    if (formRef.current?.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  return (
    <BaseModal
      title="Invite New User to Collaborate"
      maxWidth={440}
      open={true}
      onCancel={handleCloseModal}
    >
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form layout="vertical">
          <div className="modal-scroll-wrapper">
            <p>
              You can share access and rights to manage your account with
              another user. Specify their email below for a custom invitation:
            </p>
            <FormItem name="email" label="User email *">
              <Input
                name="email"
                placeholder="Enter user email"
                disabled={Boolean(linkToInvitation)}
                autoComplete="off"
              />
            </FormItem>
            {linkToInvitation && (
              <>
                <div className={styles.alertBox}>
                  <BaseSectionMessage
                    type="success"
                    className={styles.alert}
                    message={
                      <div className={styles.message}>
                        <span>Invitation link has been sent.</span>
                      </div>
                    }
                  />
                </div>
                <div className={styles.formBox}>
                  <div className={styles.formBoxInput}>
                    <span>Invitation link</span>
                    <AntdInput
                      type="text"
                      value={linkToInvitation}
                      disabled={true}
                      autoComplete="off"
                    />
                  </div>
                  <div className={styles.formBoxButton}>
                    <CopyToClipboard
                      text={linkToInvitation}
                      onCopy={handleCopyLinkToClipboard}
                    >
                      <Button>
                        <span className="icon icon-type" />
                      </Button>
                    </CopyToClipboard>
                  </div>
                </div>
              </>
            )}
            {hasInvitationLinkError && (
              <BaseSectionMessage
                type="warning"
                className={styles.alert}
                message={
                  <div className={styles.message}>
                    <span>
                      Something went wrong. The Invitation link hasn't been
                      sent.
                    </span>
                  </div>
                }
              />
            )}
          </div>
          <BaseModalFooter>
            {linkToInvitation ? (
              <Button type="primary" onClick={onClose}>
                Ok
              </Button>
            ) : (
              <>
                <Button onClick={handleCloseModal}>Cancel</Button>
                <SubmitButton type="primary">Send</SubmitButton>
              </>
            )}
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
